import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CustomerMailDialog } from "@/components/CustomerMailDialog";
import { MailLogSection } from "@/components/MailLogSection";
import { useAuth } from "@/context/AuthContext";
import { TYPE_META, KIND_META, fmtDate } from "@/pages/CustomerList";
import { Section, F, PersonFields, AdSection, NotesSection } from "@/pages/CustomerForm";
import { PartnerServices } from "@/components/PartnerServices";
import { ArrowLeft, ChevronLeft, ChevronRight, Save, Trash2, Mail, Archive, RotateCcw, CreditCard } from "lucide-react";

const EMPTY = {
  name: "", salutation: "", title: "", first_name: "", last_name: "", position: "", letter_salutation: "",
  street: "", zip: "", city: "", country: "Deutschland", phone: "", mobile: "", email: "", website: "",
  contact2: {}, partner_kind: "sponsor", ad_format_id: "", ad_format: "", ad_size: "", valid_from: "", valid_until: "", status: "offen", amount: 0, notes: "",
};

function Pager({ ids, id, go }) {
  const idx = ids.indexOf(id);
  if (idx < 0) return null;
  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => go(ids[idx - 1])} data-testid="customer-prev"><ChevronLeft size={16} /> Vorheriger</Button>
      <span className="text-sm text-slate-500 px-2" data-testid="customer-pager-pos">{idx + 1} / {ids.length}</span>
      <Button variant="outline" size="sm" disabled={idx === ids.length - 1} onClick={() => go(ids[idx + 1])} data-testid="customer-next">Nächster <ChevronRight size={16} /></Button>
    </div>
  );
}

function KindPicker({ form, set }) {
  const pickKind = (k) => set({ partner_kind: k });
  return (
    <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="customer-kind-picker">
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
        {Object.entries(KIND_META).map(([k, [l]]) => (
          <button key={k} type="button" onClick={() => pickKind(k)} data-testid={`customer-kind-${k}`}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${form.partner_kind === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
        ))}
      </div>
      <p className="text-xs text-slate-500" data-testid="customer-kind-hint">{form.partner_kind === "partner"
        ? "Bucht einzelne Leistungen wie Werbeflächen oder einen Platz auf dem Sponsorboard – eine Freianzeige nur bei Einzelvereinbarung."
        : "Vertragsleistungen laut Sponsorvertrag, inkl. Freianzeige. Vertrag und Sponsorbetrag verwaltet der Vorstand."}</p>
    </div>
  );
}

function ValiditySection({ form, set, expired }) {
  const extend = () => {
    const base = form.valid_until ? new Date(form.valid_until) : new Date();
    base.setFullYear(base.getFullYear() + 1);
    set("valid_until", base.toISOString().slice(0, 10));
  };
  return (
    <Section title="Partnerschaft" testid="section-validity">
      <F label="Gültig von" span={2} type="date" value={form.valid_from} onChange={(v) => set("valid_from", v)} testid="customer-valid-from" />
      <F label="Gültig bis" span={2} type="date" value={form.valid_until} onChange={(v) => set("valid_until", v)} testid="customer-valid-until" />
      <div className="col-span-6 sm:col-span-2 flex items-end">
        <Button type="button" variant="outline" onClick={extend} className="w-full" data-testid="customer-extend"><RotateCcw size={15} className="mr-1.5" /> Um 1 Jahr verlängern</Button>
      </div>
      {expired && (
        <div className="col-span-6 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 flex items-center gap-2" data-testid="customer-expired-banner">
          <Archive size={15} /> Partnerschaft abgelaufen am {fmtDate(form.valid_until)} – liegt im Archiv, Mailversand gesperrt. Zum Reaktivieren „Gültig bis“ verlängern und speichern.
        </div>
      )}
    </Section>
  );
}

export default function CustomerDetail({ type }) {
  const meta = TYPE_META[type];
  const { id } = useParams();
  const isNew = id === "neu";
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [ids, setIds] = useState(location.state?.ids || []);
  const [formats, setFormats] = useState([]);
  const [mailFor, setMailFor] = useState(null);
  const [logKey, setLogKey] = useState(0);
  const [tab, setTab] = useState("kontakt");
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      if (isNew) { setForm(EMPTY); setSaved(null); return; }
      const { data } = await api.get(`/customers/${id}`);
      setForm({ ...EMPTY, ...data, contact2: data.contact2 || {}, services: data.services || [] }); setSaved({ ...data, services: data.services || [] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); navigate(meta.path); }
  }, [id, isNew, navigate, meta.path]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/ad-formats").then((r) => setFormats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!location.state?.ids && !isNew) {
      api.get(`/customers?type=${type}`).then((r) => setIds(r.data.map((c) => c.id))).catch(() => {});
    }
  }, [type, isNew, location.state]);

  const set = (k, v) => setForm((f) => (typeof k === "object" ? { ...f, ...k } : { ...f, [k]: v }));
  const pending = useRef({});
  const timer = useRef(null);
  const [autoState, setAutoState] = useState("");
  const autoSave = async () => {
    const body = pending.current; pending.current = {};
    if (!Object.keys(body).length) return;
    setAutoState("saving");
    try {
      const { data } = await api.put(`/customers/${id}`, body);
      const keys = Object.fromEntries(Object.keys(body).map((key) => [key, data[key] ?? body[key]]));
      setSaved((s) => ({ ...s, ...keys, services: data.services || [] }));
      if (!Object.keys(pending.current).length) setForm((f) => ({ ...f, services: data.services || [] }));
      setAutoState("saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setForm((f) => ({ ...f, ...Object.fromEntries(Object.keys(body).map((key) => [key, saved?.[key] ?? EMPTY[key]])) }));
      setAutoState("");
    }
  };
  const setAuto = (k, v) => {
    const patch = typeof k === "object" ? k : { [k]: v };
    set(patch);
    pending.current = { ...pending.current, ...patch };
    setAutoState("pending");
    clearTimeout(timer.current);
    timer.current = setTimeout(autoSave, 700);
  };
  const set2 = (k, v) => setForm((f) => ({ ...f, contact2: { ...f.contact2, [k]: v } }));
  const strip = (o) => { const { active, archived, inactive, expired, updated_at, ...rest } = o; return rest; }; // eslint-disable-line no-unused-vars
  const dirty = saved && JSON.stringify(strip({ ...EMPTY, ...saved, contact2: saved.contact2 || {} })) !== JSON.stringify(strip(form));
  const expired = type === "partner" && !!saved?.expired;
  const inactive = saved?.active === false;
  const toggleActive = async (on) => {
    try {
      const { data } = await api.put(`/customers/${id}`, { active: on });
      const flags = { active: on, archived: !on || !!data.expired, inactive: !on };
      setSaved((s) => ({ ...s, ...flags })); setForm((f) => ({ ...f, ...flags }));
      toast.success(on ? `${meta.one} aktiviert` : `${meta.one} deaktiviert – ins Archiv verschoben`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setTier = async (tier) => {
    try {
      await api.put(`/customers/${id}`, { tier });
      setSaved((s) => ({ ...s, tier })); setForm((f) => ({ ...f, tier }));
      toast.success(tier ? `Kategorie ${tier[0].toUpperCase() + tier.slice(1)} gesetzt` : "Kategorie entfernt");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toAdvertiser = async () => {
    try {
      const { data } = await api.post(`/customers/${id}/to-advertiser`);
      toast.success(data.created ? "Als Anzeigenkunde übernommen" : "Ist bereits als Anzeigenkunde vorhanden");
      setSaved((s) => ({ ...s, advertiser_id: data.id })); setForm((f) => ({ ...f, advertiser_id: data.id }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const go = (nextId) => {
    if (dirty && !window.confirm("Ungespeicherte Änderungen verwerfen?")) return;
    navigate(`${meta.path}/${nextId}`, { state: { ids } });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Firma ist erforderlich"); return; }
    const { id: _i, created_at: _c, expired: _e, inactive: _n, archived: _a, ...rest } = form;
    const payload = { ...rest, type, amount: Number(form.amount) || 0 };
    try {
      if (isNew) {
        const { data } = await api.post("/customers", payload);
        toast.success("Angelegt");
        navigate(`${meta.path}/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/customers/${id}`, payload);
        setSaved({ ...data, services: data.services || [] }); setForm({ ...EMPTY, ...data, contact2: data.contact2 || {}, services: data.services || [] });
        toast.success("Gespeichert");
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!window.confirm(`${form.name} wirklich löschen?`)) return;
    await api.delete(`/customers/${id}`);
    toast.success("Gelöscht");
    navigate(meta.path);
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <Link to={meta.path} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="customer-back"><ArrowLeft size={15} /> {meta.title}</Link>
        {!isNew && <Pager ids={ids} id={id} go={go} />}
      </div>
      <div className="flex flex-col-reverse md:flex-row md:items-start justify-between gap-3 mb-6">
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="customer-detail-title">{isNew ? `${meta.one} anlegen` : saved?.name}</h1>
          {expired && <span className="inline-flex items-center gap-1 mt-2 mr-2 text-xs font-semibold px-2 py-1 rounded-md bg-red-100 text-red-700"><Archive size={12} /> Archiv – abgelaufen</span>}
          {inactive && <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold px-2 py-1 rounded-md bg-slate-200 text-slate-700" data-testid="customer-inactive-badge"><Archive size={12} /> Archiv – deaktiviert</span>}
          {type === "partner" && (expired || inactive) && !isNew && (
            <div className="mt-3">
              {saved?.advertiser_id
                ? <Link to={`/anzeigenkunden/${saved.advertiser_id}`} className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="customer-advertiser-link"><CreditCard size={14} /> Als Anzeigenkunde vorhanden – öffnen</Link>
                : <Button size="sm" variant="outline" onClick={toAdvertiser} data-testid="customer-to-advertiser"><CreditCard size={14} className="mr-1.5" /> Als Anzeigenkunde übernehmen</Button>}
            </div>
          )}
        </div>
        <div className="flex gap-2 items-center shrink-0 flex-wrap justify-end self-end md:self-start">
          {!isNew && type === "partner" && (
            <div className="flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="customer-tier-picker">
              {[["gold", "Gold", "bg-yellow-400 text-yellow-950"], ["silber", "Silber", "bg-slate-300 text-slate-900"], ["bronze", "Bronze", "bg-orange-300 text-orange-950"]].map(([k, l, on]) => (
                <button key={k} type="button" onClick={() => setTier(saved?.tier === k ? "" : k)} title={saved?.tier === k ? "Kategorie entfernen" : `Als ${l} kategorisieren`}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${saved?.tier === k ? on : "text-slate-500 hover:bg-slate-100"}`} data-testid={`customer-tier-${k}`}>{l}</button>
              ))}
            </div>
          )}
          {!isNew && (
            <label className="flex items-center gap-2 mr-2 text-sm font-medium text-slate-700" data-testid="customer-active-toggle-label">
              <Switch checked={saved?.active !== false} onCheckedChange={toggleActive} data-testid="customer-active-toggle" />
              {saved?.active !== false ? "Aktiv" : "Inaktiv"}
            </label>
          )}
          {!isNew && <Button variant="outline" disabled={!saved?.email || expired || inactive || !user?.is_chef} title={expired ? "Partnerschaft abgelaufen" : "E-Mail senden"} onClick={() => setMailFor(saved)} data-testid="customer-detail-mail"><Mail size={15} className="mr-1.5" /> E-Mail</Button>}
          {!isNew && <Button variant="outline" onClick={remove} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="customer-delete"><Trash2 size={15} className="mr-1.5" /> Löschen</Button>}
          <Button onClick={save} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="customer-save-button"><Save size={15} className="mr-1.5" /> Speichern{dirty ? " *" : ""}</Button>
        </div>
      </div>

      {type === "partner" && <KindPicker form={form} set={set} />}

      {type === "partner" && !isNew && <div className="flex gap-1 border-b border-slate-200 mb-5" role="tablist">
        {[["kontakt", "Kontaktdaten"], ["leistungen", `Leistungen${form.services?.length ? ` (${form.services.length})` : ""}`]].map(([k, l]) => (
          <button key={k} role="tab" onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid={`customer-tab-${k}`}>{l}</button>
        ))}
      </div>}

      {type === "partner" && !isNew && tab === "leistungen" ? <PartnerServices customerId={id} form={form} set={setAuto} formats={formats} saved={saved} autoState={autoState} /> :
      <div className="space-y-5">
        <Section title="Firma & Adresse" testid="section-company">
          <F label="Firma *" span={6} value={form.name} onChange={(v) => set("name", v)} testid="customer-name-input" />
          <F label="Straße & Hausnummer" span={6} value={form.street} onChange={(v) => set("street", v)} testid="customer-street" />
          <F label="PLZ" span={1} value={form.zip} onChange={(v) => set("zip", v)} testid="customer-zip" />
          <F label="Ort" span={3} value={form.city} onChange={(v) => set("city", v)} testid="customer-city" />
          <F label="Land" span={2} value={form.country} onChange={(v) => set("country", v)} testid="customer-country" />
          <F label="Website" span={6} value={form.website} onChange={(v) => set("website", v)} testid="customer-website" placeholder="https://" />
          {type === "bezahlkunde" && <>
            <F label="Kundennr." span={3} value={form.customer_no} onChange={(v) => set("customer_no", v)} testid="customer-no" />
            <F label="USt-IdNr." span={3} value={form.vat_id} onChange={(v) => set("vat_id", v)} testid="customer-vat" />
          </>}
        </Section>
        <Section title="Ansprechpartner" testid="section-contact">
          <PersonFields data={form} set={set} prefix="customer" />
          <F label="Briefanrede (leer = automatisch)" span={6} value={form.letter_salutation} onChange={(v) => set("letter_salutation", v)} testid="customer-letter-salutation" placeholder="z. B. Lieber Herr Muster" />
        </Section>
        <Section title="Zweiter Ansprechpartner (optional)" testid="section-contact2">
          <PersonFields data={form.contact2 || {}} set={set2} prefix="contact2" />
        </Section>
        {type === "partner" && <ValiditySection form={form} set={set} expired={expired} />}
        {(type !== "partner" || isNew) && <AdSection form={form} set={set} formats={formats} type={type} />}
        <NotesSection value={form.notes} onChange={(v) => set("notes", v)} />
        {!isNew && <MailLogSection customerId={id} customerName={saved?.name} reloadKey={logKey} />}
      </div>}
      <CustomerMailDialog customer={mailFor} onClose={() => { setMailFor(null); setLogKey((k) => k + 1); }} />
    </div>
  );
}
